import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './dashboard.css';

const API_URL = import.meta.env.VITE_API_URL;
const 曜日 = ['日', '月', '火', '水', '木', '金', '土'];

// 日付を「2025/01/15(水)」形式にフォーマット
function formatDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const w = 曜日[date.getDay()];
  return `${y}/${m}/${d}(${w})`;
}

// 時刻を「14：30」形式にフォーマット
function formatTime(date) {
  const h = String(date.getHours()).padStart(2, '0');
  const m = String(date.getMinutes()).padStart(2, '0');
  return `${h}：${m}`;
}

// "2025-01-15" + "14:30" → Date
function parseDateTime(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;
  const time = timeStr.length === 5 ? `${timeStr}:00` : timeStr;
  const date = new Date(`${dateStr}T${time}`);
  return isNaN(date) ? null : date;
}

// ISO文字列 → Date
function parseISO(iso) {
  if (!iso || typeof iso !== 'string' || !iso.includes('T')) return null;
  const date = new Date(iso);
  return isNaN(date) ? null : date;
}

// イベントの期間を表示用文字列に変換
function formatPeriod(event) {
  const { start_date, end_date, start_time, end_time, times_unified } = event;

  const isSingleDay = start_date === end_date;

  if (isSingleDay) {
    const s = parseDateTime(start_date, start_time);
    const e = parseDateTime(end_date, end_time);
    if (!s) return '—';
    if (!e) return `${formatDate(s)}　${formatTime(s)}`;
    return `${formatDate(s)}　${formatTime(s)}～${formatTime(e)}`;
  }

  if (times_unified) {
    const s = parseDateTime(start_date, start_time);
    const e = parseDateTime(end_date, end_time);
    const timePart = (s && e) ? `　${formatTime(s)}～${formatTime(e)}` : '';
    return `${formatDate(new Date(start_date))}～${formatDate(new Date(end_date))}${timePart}`;
  }

  // 日程ごとに時刻が異なる場合は日付範囲のみ
  if (!start_date) return '—';
  return `${formatDate(new Date(start_date))}～${formatDate(new Date(end_date))}`;
}

// イベント1行分の表示
function EventRow({ event, onClick }) {
  return (
    <button
      type="button"
      className="hostEvRow"
      onClick={() => onClick(event.invite_token)}
      title={event.event_title}
    >
      <div className="her-title">{event.event_title}</div>
      <div className="her-meta">{formatPeriod(event)}</div>
      <div className="her-place">
        <span className="place-marker">📍</span>
        <span>{event.event_location || '-'}</span>
      </div>
    </button>
  );
}

// メインコンポーネント
export default function HostDashboard() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // イベント一覧を取得
  async function fetchEvents() {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_URL}/api/operator/dashboard/`);
      if (res.data?.success) {
        setEvents(res.data.items || []);
      } else {
        setError('イベント一覧の取得に失敗しました');
      }
    } catch (e) {
      console.error(e);
      setError('イベント一覧の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }

  // 初回ロード
  useEffect(() => {
    fetchEvents();
  }, []);

  // 検索フィルター
  const filteredEvents = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return events;

    return events.filter(ev => {
      const text = [ev.event_title, ev.event_location, formatPeriod(ev)]
        .join(' ')
        .toLowerCase();
      return text.includes(query);
    });
  }, [events, searchQuery]);

  // エラー表示
  const renderError = () => {
    if (!error) return null;
    return <div className="error-alert">{error}</div>;
  };

  // イベントリスト
  const renderEventList = () => (
    <div className="listCard">
      <div className="listHead">
        <div className="listTitle">イベント一覧</div>
        <input
          className="listSearchInput"
          placeholder="タイトル / 場所 / 状態 などで検索"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <button className="btn" onClick={fetchEvents} disabled={loading}>
          {loading ? '更新中…' : '再読込'}
        </button>
      </div>

      <div className="listBody">
        {filteredEvents.length === 0 && !loading && (
          <div className="empty">該当するイベントがありません</div>
        )}
        {filteredEvents.map(ev => (
          <EventRow
            key={ev.invite_token}
            event={ev}
            onClick={(token) => navigate(`/operator/event/${token}`)}
          />
        ))}
      </div>
    </div>
  );

  return (
    <div className="hostDash">
      {/* エラー表示 */}
      {renderError()}
      
      {/* イベントリスト */}
      {renderEventList()}
    </div>
  );
}